// A clinic's account on the lab side: every case's charge and what has been paid against it, a
// Collect button per case, and the full statement underneath. Nothing is computed here that the
// statement does not already say - per-case figures are the statement's own entries grouped by case,
// so this page, the Billing console and the doctor's portal statement can never disagree.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  formatDate,
  formatMoney,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
  rupeesToPaisa,
  WF_LEDGER_ENTRY_LABELS,
  type PaymentMethod,
  type WfBankAccount,
  type WfCase,
  type WfLedgerEntry,
  type WfPayment,
  type WfStatement,
} from '@alignova/shared';
import { Link } from 'react-router-dom';
import { useCan } from '../../auth/useCan.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { Card } from '../../ui/primitives.tsx';
import { wfApi } from '../workflow/workflowApi.ts';
import { Btn, ErrorText, Field, Modal, formGridCls, inputCls } from '../workflow/wfUi.tsx';

interface CaseRow {
  caseId: string;
  caseNumber: string;
  patientName: string;
  charged: number;
  received: number;
  pending: number;
  balance: number;
}

export function ClinicLedger({ doctorId, cases }: { doctorId: string; cases: WfCase[] }) {
  const can = useCan();
  const [s, setS] = useState<WfStatement | null>(null);
  const [pending, setPending] = useState<WfPayment[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [collecting, setCollecting] = useState<CaseRow | null>(null);
  const [editing, setEditing] = useState<WfLedgerEntry | null>(null);
  const canEdit = can('invoice:manage');

  const load = useCallback(() => {
    Promise.all([wfApi.statement(doctorId), wfApi.billingPayments({ doctorId, status: 'pending' })])
      .then(([st, p]) => {
        setS(st);
        setPending(p);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [doctorId]);
  useEffect(load, [load]);
  useLiveEvent('workflow.billing', load);

  const rows = useMemo<CaseRow[]>(() => {
    const byCase = new Map<string, CaseRow>();
    const row = (caseId: string, caseNumber: string, patientName = '') => {
      let r = byCase.get(caseId);
      if (!r) byCase.set(caseId, (r = { caseId, caseNumber, patientName, charged: 0, received: 0, pending: 0, balance: 0 }));
      return r;
    };
    for (const c of cases) row(c.id, c.caseNumber, c.patientName ?? '');
    for (const e of s?.entries ?? []) {
      if (e.isVoid || !e.caseId) continue;
      const r = row(e.caseId, e.caseNumber ?? '');
      r.charged += e.debit;
      r.received += e.credit;
    }
    for (const p of pending) if (p.caseId && byCase.has(p.caseId)) byCase.get(p.caseId)!.pending += p.amount;
    const out = [...byCase.values()];
    for (const r of out) r.balance = r.charged - r.received;
    // Money owed first, then anything with activity, then the rest in case order.
    return out.sort((a, b) => b.balance - a.balance || b.charged - a.charged || b.caseNumber.localeCompare(a.caseNumber));
  }, [cases, s, pending]);

  if (!can('finance:read')) return null;
  const onAccount = pending.filter((p) => !p.caseId).reduce((n, p) => n + p.amount, 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <h2 className="text-lg font-semibold text-ink">Ledger</h2>
        {s && (
          <div className="ml-auto flex flex-wrap gap-2">
            <Stat label="Charged" value={formatMoney(s.totals.charges)} />
            <Stat label="Received" value={formatMoney(s.totals.payments + s.totals.adjustments)} />
            <Stat label="Outstanding" value={formatMoney(s.totals.closingBalance)} tone={s.totals.closingBalance > 0 ? 'bad' : 'good'} />
            {s.pendingAmount > 0 && <Stat label={`Awaiting verification (${s.pendingCount})`} value={formatMoney(s.pendingAmount)} tone="warn" />}
          </div>
        )}
      </div>
      <ErrorText>{err}</ErrorText>

      {/* Case-wise: what each case was charged and what has come in against it. */}
      <Card className="overflow-x-auto p-0">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left text-[11px] uppercase tracking-wider text-muted">
              <th className="px-4 py-3 font-medium">Case</th>
              <th className="px-4 py-3 font-medium">Patient</th>
              <th className="px-4 py-3 text-right font-medium">Charged</th>
              <th className="px-4 py-3 text-right font-medium">Received</th>
              <th className="px-4 py-3 text-right font-medium">Balance</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.caseId} className="border-b border-line hover:bg-surface-2">
                <td className="px-4 py-2 font-mono text-xs">
                  <Link className="text-accent underline" to={`/workflow/cases/${r.caseId}`}>
                    {r.caseNumber}
                  </Link>
                </td>
                <td className="px-4 py-2">{r.patientName}</td>
                <td className="px-4 py-2 text-right font-mono">{r.charged ? formatMoney(r.charged) : <span className="text-muted">not billed</span>}</td>
                <td className="px-4 py-2 text-right font-mono">
                  {r.received ? formatMoney(r.received) : ''}
                  {r.pending > 0 && <div className="text-[11px] text-amber-700">+{formatMoney(r.pending)} pending</div>}
                </td>
                <td className={`px-4 py-2 text-right font-mono font-semibold ${r.balance > 0 ? 'text-status-urgent-fg' : ''}`}>{r.charged || r.received ? formatMoney(r.balance) : ''}</td>
                <td className="px-4 py-2 text-right">
                  {can('invoice:manage') && (
                    <Btn tone="subtle" onClick={() => setCollecting(r)}>
                      Collect
                    </Btn>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted">
                  No cases yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
      {onAccount > 0 && <p className="text-xs text-muted">{formatMoney(onAccount)} pending on account (not tied to a case).</p>}

      {/* The statement itself - every charge and payment in date order, with the running balance. */}
      <Card className="max-h-[60vh] overflow-auto p-0">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 bg-surface">
            <tr className="border-b border-line text-left text-[11px] uppercase tracking-wider text-muted">
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Details</th>
              <th className="px-4 py-3 text-right font-medium">Charge</th>
              <th className="px-4 py-3 text-right font-medium">Received</th>
              <th className="px-4 py-3 text-right font-medium">Balance</th>
              {canEdit && <th className="px-2 py-3" />}
            </tr>
          </thead>
          <tbody>
            {s?.entries.map((e) => (
              <tr key={e.id} className={`border-b border-line ${e.isVoid ? 'text-muted line-through' : ''}`}>
                <td className="whitespace-nowrap px-4 py-2">{formatDate(e.entryDate)}</td>
                <td className="px-4 py-2">
                  <div className="text-ink">{e.description}</div>
                  <div className="text-[11px] text-muted">
                    {WF_LEDGER_ENTRY_LABELS[e.entryType]}
                    {e.caseNumber ? ` · ${e.caseNumber}` : ''}
                    {e.createdByName ? ` · ${e.createdByName}` : ''}
                    {e.isVoid ? ` · Voided: ${e.voidReason ?? ''}` : ''}
                  </div>
                </td>
                <td className="px-4 py-2 text-right font-mono">{e.debit ? formatMoney(e.debit) : ''}</td>
                <td className="px-4 py-2 text-right font-mono">{e.credit ? formatMoney(e.credit) : ''}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{formatMoney(e.balance)}</td>
                {canEdit && (
                  <td className="px-2 py-2 text-right">
                    {!e.isVoid && (
                      <button type="button" className="text-xs font-medium text-accent hover:underline" onClick={() => setEditing(e)}>
                        Edit
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {s && s.entries.length === 0 && (
              <tr>
                <td colSpan={canEdit ? 6 : 5} className="px-4 py-8 text-center text-muted">
                  Nothing on this clinic&rsquo;s account yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      {editing && (
        <EditEntryModal
          entry={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            load();
          }}
        />
      )}
      {collecting && (
        <CollectModal
          doctorId={doctorId}
          row={collecting}
          canVerify={can('finance:verify_payment')}
          onClose={() => setCollecting(null)}
          onDone={() => {
            setCollecting(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: 'neutral' | 'bad' | 'good' | 'warn' }) {
  const tones = { neutral: 'border-line', bad: 'border-status-urgent-fg/40 bg-status-urgent-bg/40', good: 'border-status-progress-fg/30 bg-status-progress-bg/40', warn: 'border-amber-400/50 bg-amber-50' }[tone];
  return (
    <div className={`rounded-lg border px-3 py-1.5 ${tones}`}>
      <div className="font-mono text-base font-semibold text-ink">{value}</div>
      <div className="text-[11px] text-muted">{label}</div>
    </div>
  );
}

/**
 * Take money against one case. Recorded like any lab-side payment; someone allowed to verify can
 * credit it straight away (they are the one holding the money), otherwise it waits in the Billing
 * queue like every other claim.
 */
function CollectModal({ doctorId, row, canVerify, onClose, onDone }: { doctorId: string; row: CaseRow; canVerify: boolean; onClose: () => void; onDone: () => void }) {
  const due = Math.max(0, row.balance - row.pending);
  const [accounts, setAccounts] = useState<WfBankAccount[]>([]);
  const [f, setF] = useState({ amount: due ? String(due / 100) : '', method: 'cash' as PaymentMethod, bankAccountId: '', reference: '', paidOn: new Date().toISOString().slice(0, 10), note: '' });
  const [creditNow, setCreditNow] = useState(canVerify);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));
  const paisa = rupeesToPaisa(f.amount);
  useEffect(() => {
    wfApi.bankAccounts().then(setAccounts).catch(() => setAccounts([]));
  }, []);

  const save = async () => {
    if (!paisa || paisa <= 0) {
      setErr('Enter the amount received.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const p = await wfApi.recordPayment({
        doctorId,
        caseId: row.caseId,
        amount: paisa,
        method: f.method,
        bankAccountId: f.bankAccountId || null,
        reference: f.reference || null,
        paidOn: f.paidOn,
        note: f.note || null,
      });
      if (creditNow) await wfApi.verifyPayment(p.id);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Collect payment - ${row.caseNumber}`}
      footer={
        <>
          <Btn tone="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Collect'}
          </Btn>
        </>
      }
    >
      <div className={formGridCls}>
        <ErrorText>{err}</ErrorText>
        <p className="text-sm text-muted">
          {row.patientName ? `${row.patientName} · ` : ''}
          {row.charged ? `Charged ${formatMoney(row.charged)}, received ${formatMoney(row.received)}` : 'This case has not been billed yet - this is taken as an advance.'}
          {row.pending ? `, ${formatMoney(row.pending)} awaiting verification` : ''}.
        </p>
        <Field label="Amount (PKR) *" hint={paisa && paisa > 0 ? `Collecting ${formatMoney(paisa)}` : undefined}>
          <input className={inputCls} inputMode="decimal" autoFocus value={f.amount} onChange={(e) => set('amount', e.target.value)} />
        </Field>
        <Field label="Method *">
          <select className={inputCls} value={f.method} onChange={(e) => set('method', e.target.value)}>
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_LABELS[m]}
              </option>
            ))}
          </select>
        </Field>
        {accounts.length > 0 && (
          <Field label="Into account">
            <select className={inputCls} value={f.bankAccountId} onChange={(e) => set('bankAccountId', e.target.value)}>
              <option value="">Not applicable</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label} - {a.bankName}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Date paid *">
          <input type="date" className={inputCls} value={f.paidOn} onChange={(e) => set('paidOn', e.target.value)} />
        </Field>
        <Field label="Reference">
          <input className={inputCls} value={f.reference} onChange={(e) => set('reference', e.target.value)} />
        </Field>
        <Field label="Note">
          <textarea className={inputCls} rows={2} value={f.note} onChange={(e) => set('note', e.target.value)} />
        </Field>
        {canVerify ? (
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={creditNow} onChange={(e) => setCreditNow(e.target.checked)} />
            Money received - credit the ledger now
          </label>
        ) : (
          <p className="text-xs text-muted">Filed as pending - it credits the ledger once verified in Billing.</p>
        )}
      </div>
    </Modal>
  );
}

/**
 * Change a ledger line's amount. The server corrects whatever the line stands for - an invoice is
 * re-issued (Adjustment line), a payment is replaced and re-verified, a hand entry is re-posted -
 * so the clinic's portal statement and invoices show the same change. Shared with the Billing console.
 */
export function EditEntryModal({ entry, onClose, onDone }: { entry: WfLedgerEntry; onClose: () => void; onDone: () => void }) {
  const current = entry.debit || entry.credit;
  const [amount, setAmount] = useState(String(current / 100));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const paisa = rupeesToPaisa(amount);
  const what =
    entry.entryType === 'payment'
      ? 'The original payment is reversed and a corrected payment is recorded and verified in its place.'
      : entry.description.startsWith('INV-')
        ? 'The invoice is re-issued with an Adjustment line for the difference; the old invoice is voided.'
        : 'This line is voided and re-posted at the new amount.';

  const save = async () => {
    if (!paisa || paisa <= 0) return setErr('Enter an amount above zero.');
    if (reason.trim().length < 3) return setErr('Say why the amount is changing.');
    setBusy(true);
    setErr(null);
    try {
      const r = await wfApi.editEntry(entry.id, paisa, reason.trim());
      if (r.kind === 'payment' && r.pendingVerification) setDone(`Recorded ${r.to} - it is waiting in the verification queue.`);
      else onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={done ? onDone : onClose}
      title="Edit amount"
      footer={
        done ? (
          <Btn onClick={onDone}>Done</Btn>
        ) : (
          <>
            <Btn tone="ghost" onClick={onClose}>
              Cancel
            </Btn>
            <Btn disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save'}
            </Btn>
          </>
        )
      }
    >
      <div className={formGridCls}>
        <p className="text-sm text-ink">
          {entry.description}
          {entry.caseNumber ? ` · ${entry.caseNumber}` : ''} - currently <span className="font-mono">{formatMoney(current)}</span>
        </p>
        <p className="text-xs text-muted">{what} The clinic sees the change in their portal straight away.</p>
        <ErrorText>{err}</ErrorText>
        {done ? (
          <p className="text-sm text-ink">{done}</p>
        ) : (
          <>
            <Field label="New amount (PKR) *" hint={paisa && paisa > 0 ? formatMoney(paisa) : undefined}>
              <input className={inputCls} inputMode="decimal" autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} />
            </Field>
            <Field label="Reason *">
              <input className={inputCls} placeholder="e.g. price agreed with the doctor" value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
          </>
        )}
      </div>
    </Modal>
  );
}
